import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import UserContext from '@/components/UserContext';
import LoginDialog from '@/components/LoginDialog';
import { getInitials } from '@/components/ProfileButton';
import { LogoColor } from '@/components/Logo';
import { User as UserIcon, Images, LogOut, Code2, Upload } from 'lucide-react';
import { cn } from '@/utils';
import { ACCEPTED_IMAGE_ACCEPT, validateImageFile } from '@/components/vectorizer/UploadZone';

/** sessionStorage key used to hand an imported image to the home page on other routes. */
export const PENDING_UPLOAD_KEY = 'svgnew_pending_upload';
// Files above this are not stashed (sessionStorage quota is ~5MB of UTF-16 and
// base64 inflates ~37%); we just navigate home so the user can drop it there.
const PENDING_UPLOAD_MAX_BYTES = 4 * 1024 * 1024;

interface SiteHeaderProps {
  hidePricing?: boolean;
  onLogoClick?: () => void;
  inEditor?: boolean;
  /** When provided (home page), imported files are handed over directly. Otherwise the file is stashed and we navigate home. */
  onImport?: (file: File) => void;
}

export default function SiteHeader({ hidePricing, onLogoClick, inEditor, onImport }: SiteHeaderProps = {}) {
  const { user, supabase } = useContext(UserContext);
  const [showLogin, setShowLogin] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  // Import button is accent-styled on the homepage empty state, ghost elsewhere
  const isHomepage = router.pathname === '/' && !inEditor;

  const openUploadPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Inline import errors fade on their own.
  useEffect(() => {
    if (!importError) return;
    const t = setTimeout(() => setImportError(null), 5000);
    return () => clearTimeout(t);
  }, [importError]);

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const result = validateImageFile(file);
    if (!result.ok) {
      setImportError(result.message);
      return;
    }
    setImportError(null);
    if (onImport) {
      onImport(file);
      return;
    }
    // Other pages: hand the file to the home page through sessionStorage.
    if (file.size > PENDING_UPLOAD_MAX_BYTES) {
      router.push('/');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        sessionStorage.setItem(PENDING_UPLOAD_KEY, reader.result as string);
      } catch {
        // Over quota — fall through and let the user drop it on the home page.
      }
      router.push('/');
    };
    reader.onerror = () => setImportError("Couldn't read that file. Try another image.");
    reader.readAsDataURL(file);
  };


  return (
    <>
      {/* Header-owned picker so import / U work on every page, including the editor. */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_ACCEPT}
        onChange={handleImportFile}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
      />
      <header className="border-b border-cream-200 bg-cream-50 sticky top-0 z-50">
        <div className="px-4 sm:px-5 h-14 flex items-center gap-4 sm:gap-5">
          {/* Logo — compact */}
          <Link
            href="/"
            aria-label="SVG.new home"
            className="flex items-center shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50"
            onClick={onLogoClick}
          >
            <LogoColor className={cn('!gap-0 [&>svg]:block', isHomepage ? 'w-[68px]' : 'w-[56px]')} />
          </Link>

          <div className="flex-1" />

          {/* Right nav */}
          <div className="flex items-center gap-0.5 sm:gap-1">
            {!hidePricing && (
              <>
                <Link
                  href="/developers"
                  className="hidden md:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] text-ink-muted hover:text-ink hover:bg-cream-100 transition-colors"
                >
                  <Code2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  API
                </Link>
                <Link
                  href="/creations"
                  className="hidden md:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] text-ink-muted hover:text-ink hover:bg-cream-100 transition-colors"
                >
                  <Images className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Images
                </Link>
                <Link
                  href="/pricing"
                  className="hidden sm:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] text-ink-muted hover:text-ink hover:bg-cream-100 transition-colors"
                >
                  Pricing
                </Link>
                <div className="hidden sm:block w-px h-4 bg-cream-200 mx-1.5" />

                {/* Import — accent on homepage, ghost elsewhere */}
                <div className="relative">
                <button
                  type="button"
                  onClick={openUploadPicker}
                  className={cn(
                    'inline-flex items-center gap-1.5 h-10 sm:h-8 px-3 rounded-md text-[13px] font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50',
                    isHomepage
                      ? 'bg-sky text-sky-ink hover:bg-sky-soft'
                      : 'text-ink-soft border border-cream-300 bg-white hover:bg-cream-100 hover:border-cream-400'
                  )}
                >
                  <Upload className="w-3.5 h-3.5" strokeWidth={2} />
                  Import
                </button>
                {importError && (
                  <p
                    role="alert"
                    className="absolute right-0 top-full mt-2 w-64 rounded-md border border-cream-300 bg-white px-3 py-2 text-[12px] leading-relaxed text-clay shadow-sm"
                  >
                    {importError}
                  </p>
                )}
                </div>
              </>
            )}


            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger aria-label="Account menu" className="text-[10px] font-mono font-semibold flex items-center justify-center rounded-md w-8 h-8 outline-none focus-visible:ring-2 focus-visible:ring-sky focus-visible:ring-offset-2 focus-visible:ring-offset-cream-50 border border-cream-300 bg-[var(--panel-2)] text-ink hover:bg-cream-200 hover:border-cream-400 transition-colors ml-1">
                  {getInitials(user)}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-44 text-[13px]" side="bottom" align="end" sideOffset={8}>
                  <DropdownMenuItem asChild className="cursor-pointer sm:hidden">
                    <Link href="/pricing">Pricing</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="sm:hidden" />
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link href="/account">
                      <UserIcon className="w-3.5 h-3.5 mr-2" />
                      Account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link href="/creations">
                      <Images className="w-3.5 h-3.5 mr-2" />
                      Images
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      // Clear the server cookie before leaving so the next page load
                      // isn't still signed in server-side.
                      await supabase.auth.signOut().catch(() => {});
                      await fetch('/api/auth', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'same-origin',
                        body: JSON.stringify({ event: 'SIGNED_OUT', session: null }),
                      }).catch(() => {});
                      window.location.href = '/';
                    }}
                    className="cursor-pointer text-ink-muted"
                  >
                    <LogOut className="w-3.5 h-3.5 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={() => setShowLogin(true)}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[13px] text-ink-muted hover:text-ink hover:bg-cream-100 transition-colors ml-1"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </header>

      <LoginDialog
        supabase={supabase}
        isSignIn
        open={showLogin}
        // Google and email links come back to the page the visitor signed in from.
        redirectTo={router.asPath}
        onSignedIn={() => setShowLogin(false)}
        onClose={() => setShowLogin(false)}
      />
    </>
  );
}
